<template>
  <v-main class="pt-0">
    <AlertsComponent context="payment" />
    <v-container class="maxContainerWidth">
      <!-- My Primerica Header -->
      <v-row class="text-white mt-4 mb-4">
        <v-col class="mr-4 d-flex align-center">
          <img src="../../assets/ChangeBankAccount.svg" alt="Change Billing" width="48" />
          <h1 class="text-white pl-2">
            {{ $t('L_change_bank_account') }}
          </h1>
        </v-col>
      </v-row>
      <MessageDialog
        :show-dialog="dialog"
        :has-button-two="true"
        type="warning"
        :title="$t('L_cancel_payment_process')"
        :message="$t('L_do_you_want_to_cancel_the_current_payment_process_and_not_submit')"
        :button-two-label="$t('L_yes')"
        :button-one-label="$t('L_no')"
        :is-persistent="true"
        @buttonTwoAction="closeAndGoToPolicyDetails"
        @buttonOneAction="dialog = false"
      />
      <!-- Section Guide
      One Time Payment -    1 Payment Amount  2 Payment Method  3 Welcome Back
      ACH              -    1 Payment Amount  2 Payment Method  3 Review Payment 4 Confirmation
      -->
      <!-- This is for screen larger than xsOnly Horizontal Mode -->
      <v-stepper v-model="step" :vertical="$vuetify?.display?.xs">
        <v-stepper-header v-show="!$vuetify?.display?.xs">
          <v-stepper-item :complete="step > 1" value="1" class="stepperTextSize">
            {{ $t('L_bank_information') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 2" value="2" class="stepperTextSize">
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 3" value="3" class="stepperTextSize">
            {{ $t('L_confirmation') }}
          </v-stepper-item>
        </v-stepper-header>
        <v-stepper-window>
          <v-stepper-item
            :complete="step > 1"
            value="1"
            v-show="$vuetify?.display?.xs"
            class="pa-0 ma-0 mt-4 mb-4"
            :class="step === '1' ? 'my-step-complete' : ''"
          >
            {{ $t('L_bank_information') }}
          </v-stepper-item>
          <v-stepper-window-item value="1" class="pl-0 pr-0 pt-0">
            <BankAccountTypeChangeStepOne
              :show-date-picker="showDatePicker"
              :policy-data="currentPolicy"
              @goToStepTwo="goToStepTwo"
              @cancel="closeAndGoToPolicyDetails"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step > 2"
            value="2"
            v-show="$vuetify?.display?.xs"
            class="pa-0 ma-0 mt-4 mb-4"
            :class="step === '2' ? 'my-step-complete' : ''"
          >
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-stepper-window-item value="2" class="pl-0 pr-0 pt-0">
            <BankAccountTypeChangeStepTwo
              :is-update-bank="isUpdateBank"
              :bank-info="bankInfo"
              @goToStepOne="goToStepOne"
              @goToStepThree="goToStepThree"
              @cancel="closeAndGoToPolicyDetails"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step > 3"
            value="3"
            v-show="$vuetify?.display?.xs"
            class="pa-0 ma-0 mt-4 mb-4"
            :class="step === '3' ? 'my-step-complete' : ''"
          >
            {{ $t('L_confirmation') }}
          </v-stepper-item>
          <v-stepper-window-item value="3" class="pl-0 pr-0 pt-0">
            <BankAccountTypeChangeStepThree :bank-info="bankInfo" @done="goToStepFour" />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-container>
    <FooterComponent />
    <NavigationConfirmationDialog
      v-if="showNavigationConfirmationDialog"
      @confirm-navigation="navigateToRoute"
      @cancel-navigation="showNavigationConfirmationDialog = false"
    />
  </v-main>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapActions } from 'pinia';
import { currency } from '@/shared/util/formatters';
import MessageDialog from '@/components/MessageDialog.vue';
import BankAccountTypeChangeStepOne from './BankAccountTypeChangeStepOne.vue';
import BankAccountTypeChangeStepTwo from './BankAccountTypeChangeStepTwo.vue';
import BankAccountTypeChangeStepThree from './BankAccountTypeChangeStepThree.vue';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { RouteNames } from '@/router/routeNames';
// Import the IPolicyBankInfo type
import type { IPolicyBankInfo } from '@/modules/policy/store/policyStore.model';
import FooterComponent from '@/components/Footer/Footer.vue';
import NavigationConfirmationDialog from '@/components/NavigationConfirmation/NavigationConfirmationDialog.vue';
import AlertsComponent from '@/components/AlertsComponent.vue';
import type { RouteParamsGeneric } from 'vue-router';

const componentOptions: ComponentOptions = {
  name: 'BankAccountTypeChange',
  components: {
    BankAccountTypeChangeStepOne,
    BankAccountTypeChangeStepTwo,
    BankAccountTypeChangeStepThree,
    MessageDialog,
    FooterComponent,
    NavigationConfirmationDialog,
    AlertsComponent,
  },
  data: () => ({
    showDatePicker: false,
    isUpdateBank: true,
    persistent: true,
    dialog: false,
    step: '1',
    bankInfo: {},
    selectedMethod: '', // Checking || Savings
    showNavigationConfirmationDialog: false,
    navigatingToRoute: '',
    navigatingToParams: {} as RouteParamsGeneric,
    navigationAwayConfirmed: false,
  }),
  computed: {
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'currentPolicy',
      'selectedPolicyIndex',
      'paymentData',
      'changeBankAccountStep',
    ]),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
      getAgent: 'getAgent',
    }),
    getTotalDue() {
      return currency(+this.currentPolicy?.paynowInfo?.totalPaymentAmount, 2);
    },
    getMinimumDue() {
      return currency(+this.currentPolicy?.paynowInfo?.minimumPaymentAmount, 2);
    },
  },
  methods: {
    ...mapActions(usePolicyStore, ['setChangeBankAccountStep', 'resetBankInfo']),
    navigateToRoute() {
      this.navigationAwayConfirmed = true;
      this.$router.push({
        name: this.navigatingToRoute,
        params: this.navigatingToParams,
      });
    },
    goToStepOne() {
      this.step = '1'; // vue3
    },
    goToStepTwo(bankInfo: IPolicyBankInfo | undefined) {
      this.bankInfo = bankInfo;
      this.step = '2'; // vue3
    },
    goToStepThree() {
      this.step = '3'; // vue3
    },
    async goToStepFour() {
      this.setChangeBankAccountStep(-1);
      this.step = '-1';
      await this.$router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: this.selectedPolicyIndex },
      });
    },
    async closeAndGoToPolicyDetails() {
      this.navigationAwayConfirmed = true;
      this.setChangeBankAccountStep(-1);
      await this.$router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: this.selectedPolicyIndex },
      });
    },
  },
  async mounted() {
    // Clearing out the Bank Name from previous session
    // await this.$store.dispatch('policy/resetBankInfo', '');
    await this.resetBankInfo('');
  },
  beforeRouteLeave(to, _, next) {
    const isProcessDone = this.changeBankAccountStep === -1 || this.navigationAwayConfirmed;
    // const navigatingOutsideMinorAccountLink =
    //   to.name !== RouteNames.MINOR_ACCOUNT_LINK_PROCESS &&
    //   to.path !== '/minor-account-link-process';
    // navigatingOutsideMinorAccountLink &&
    if (!isProcessDone) {
      this.showNavigationConfirmationDialog = true; // show NavigationConfirmationDialog
      this.navigatingToRoute = to.name as string; // save the route name
      this.navigatingToParams = to.params;
      next(false); // cancel the navigation
    } else {
      this.setChangeBankAccountStep(-1); // reset the step when navigation happens
      next();
    }
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 600px) {
  .v-stepper-window {
    margin: 0rem !important;
  }
}
.maxContainerWidth {
  max-width: 1024px;
  margin: auto;
}

@media only screen and (max-width: 959px) {
  .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
    display: block !important;
    padding-left: 5px;
  }
}
.v-stepper__header {
  overflow: auto;
  display: flex !important;
  flex-wrap: nowrap;
  justify-content: left;
}
</style>
